import { observer } from 'mobx-react'
import { useShowDialog } from '../../App'
import type { ExistingDiscrepancyReport } from '../../models/DiscrepancyReports/DiscrepancyReportsModel'
import { getLinkForVehiclesPageWithSelectedVehicleDiscrepancyReports, getLinkForVehiclesPageWithSelectedVehicleAndDiscrepancyReport } from '../../router'
import { getFormattedDateTimeInUserLocaleAndUTC } from '../../utils/datetimeUtils'
import { SecondaryButton } from '../Button/Button'
import { SvgIcons } from '../Icon/Icon'
import { TextInput } from '../Input/Input'
import { DiscrepancyReportStatusIndicator } from '../StatusIndicator/StatusIndicator'
import { ScrollableTable, TableTitle, TableTitleRow, Thead, Tr, Th, Tbody, Td, TrWithLink, Column, ExtendedViewTr } from '../Table/Table'
import { theme } from '../../styles/theme'
import styled from 'styled-components'

interface VehicleDiscrepancyReportsTableProps {
    vehicleId: number,
    vehicleName: string,
    vehicleDiscrepancyReports: Array<ExistingDiscrepancyReport>
    selectedDiscrepancyReportId: number | null
    resolveDiscrepancyReport: (reportId: number) => Promise<void>
}
export const VehicleDiscrepancyReportsTable = observer(({
    vehicleId, 
    vehicleName,
    vehicleDiscrepancyReports,
    selectedDiscrepancyReportId,
    resolveDiscrepancyReport,
}: VehicleDiscrepancyReportsTableProps) => {
    return (<>
        <TableTitleRow>
            <TableTitle {...{ entityCount: vehicleDiscrepancyReports.length, entityName: 'Discrepancy report' }} />
        </TableTitleRow>
        { vehicleDiscrepancyReports.length > 0 && <ScrollableTable>
            <Thead>
                <Tr>
                    <Th>ID</Th>
                    <Th>Status</Th>
                    <Th>Created by</Th>
                    <Th>Date created (UTC)</Th>
                    <Th>Resolved by by</Th>
                    <Th>Date resolved (UTC)</Th>
                </Tr>
            </Thead>
            <Tbody>
                {
                    vehicleDiscrepancyReports.map(discrepancyReport => 
                        <VehicleDiscrepancyReportRow {...{ 
                            key: discrepancyReport.id,
                            discrepancyReport, 
                            vehicleId,
                            vehicleName,
                            isSelected: selectedDiscrepancyReportId === discrepancyReport.id,
                            onResolveDiscrepancyReport: () => {
                                void resolveDiscrepancyReport(discrepancyReport.id)
                            }
                        }} />
                    )
                }
            </Tbody>
        </ScrollableTable> }
    </>)
})

interface VehicleDiscrepancyReportRowProps {
    vehicleId: number
    vehicleName: string
    isSelected: boolean
    discrepancyReport: ExistingDiscrepancyReport
    onResolveDiscrepancyReport: () => void
}
const VehicleDiscrepancyReportRow = observer(({ isSelected, discrepancyReport, vehicleId, vehicleName, onResolveDiscrepancyReport, ...props } : VehicleDiscrepancyReportRowProps) => {
    const rowLink = isSelected ?
            getLinkForVehiclesPageWithSelectedVehicleDiscrepancyReports(vehicleId) :
            getLinkForVehiclesPageWithSelectedVehicleAndDiscrepancyReport(vehicleId, discrepancyReport.id)
    const showDialog = useShowDialog()
    return (
        <>
            <TrWithLink {...{isSelectable: true, isSelected, route: rowLink, ...props}}>
                <Td>{ discrepancyReport.id }</Td>
                <Td><DiscrepancyReportStatusIndicator {...{ discrepancyReportStatus: discrepancyReport.reportStatus }} /></Td>
                <Td>{ discrepancyReport.created_by?.name }</Td>
                <Td>{ discrepancyReport.creationDate && getFormattedDateTimeInUserLocaleAndUTC(discrepancyReport.creationDate) }</Td>
                <Td>{ discrepancyReport.resolved_by?.name }</Td>
                <Td>{ discrepancyReport.resolutionDate && getFormattedDateTimeInUserLocaleAndUTC(discrepancyReport.resolutionDate) }</Td>
            </TrWithLink>
            {
                isSelected && <DiscrepancyReportTextArea {...{ 
                    discrepancyReport,
                    onResolveClicked: () => {
                        showDialog({
                            title: 'Resolve report?',
                            text: `Discrepancy report ${discrepancyReport.id} for vehicle ${vehicleName} will be resolved, and the report details will no longer be editable. This action is irreversible.`,
                            confirmText: 'Resolve',
                            onConfirmed: onResolveDiscrepancyReport,
                            onCancelled: () => {},
                        })
                    }
                }}/>
            }
        </>
    )
    })

interface DiscrepancyReportTextAreaProps {
    discrepancyReport: ExistingDiscrepancyReport
    onResolveClicked: () => void
}
const DiscrepancyReportTextArea = observer(({ discrepancyReport, onResolveClicked } : DiscrepancyReportTextAreaProps) => {
   return (<ExtendedViewTr isSelected={true}>
        <Td colSpan={6}>
            <DiscrepancyReportDetailsContainer>
                <span></span>
                <TextInput {...{
                    inputStyle: 'view-only',
                    name: 'description',
                    label: 'Report Details',
                    value: discrepancyReport.description,
                    isDisabled: true
                }} />
                { !discrepancyReport.is_resolved &&                 
                    <SecondaryButton {...{ 
                        buttonStyle: 'thin',
                        style: {
                            alignSelf: 'end'
                        },
                        startComponent: <SvgIcons.CheckmarkSvg />,
                        text: 'Resolve', 
                        onClick: onResolveClicked 
                     }}/>     
                }
            </DiscrepancyReportDetailsContainer>
        </Td>
    </ExtendedViewTr>)
})

const DiscrepancyReportDetailsContainer = styled(Column)`
    border: solid ${theme.borderWidths.thin} ${theme.colors.MediumGrey};
    padding: 1em;
    background-color: ${theme.colors.LightBackground};
    > ${TextInput} {
        border: none;
        color: green;
    }
`